import { useTheme } from "styled-components";
import React, { CSSProperties } from "react";
import CheckOutlined from "@ant-design/icons/CheckOutlined";
import PlusOutlined from "@ant-design/icons/PlusOutlined";
import Flex from "common/Boxes/Flex";
import Tag from "common/Tags/Tag";
import { FilterSectionData } from "@shared/features/filters/types";
import { StringArrayFilterType, TagName } from "@shared/types";
import { TagNameToColor } from "@shared/common/constants";
import styled from "styled-components";
import { getAllSelectedDisplay } from "@shared/features/filters/utils";
import Popover from "common/Popover/Popover";
import { MobileResetButton, StyledInput } from "common/Filters/common/styles";
import { useBottomNavigator } from "utils/hooks/misc";
import BottomDrawer from "common/Drawers/BottomDrawer";

const TagsFiltersSection = ({
  options,
  toggleItem,
  getIsSelected,
  isChipsView,
  isSingleRowView,
  getChipsText,
  value,
  title,
  unselectAll,
  allSelected,
  resetButtonData,
}: FilterSectionData<StringArrayFilterType>) => {
  const bottomNavigatorData = useBottomNavigator();
  const isBottomNavigator = bottomNavigatorData.shouldDisplay;

  const content = (
    <>
      <Flex
        $justifyContent="flex-start"
        style={{
          gridGap: 8,
          padding: "8px 0",
          flexWrap: "wrap",
          maxWidth: isBottomNavigator ? "100%" : 250,
        }}
      >
        {options.map((tagName) => (
          <TagButton
            key={tagName}
            name={tagName as TagName}
            isSelected={getIsSelected(tagName)}
            onClick={() => toggleItem(tagName)}
          />
        ))}
      </Flex>
      {isBottomNavigator && isSingleRowView && (
        <MobileResetButton
          onClick={resetButtonData.onClick}
          style={{ marginTop: 16 }}
          disabled={allSelected}
        >
          {resetButtonData.resetText}
        </MobileResetButton>
      )}
    </>
  );

  const numSelectedTags = value.length;

  const getPlaceholder = () => {
    if (isChipsView && isSingleRowView) return getChipsText();
    return !numSelectedTags
      ? getAllSelectedDisplay(title, isSingleRowView)
      : "";
  };

  let inputValue: string | undefined;
  if (numSelectedTags) {
    if (numSelectedTags === 1) {
      inputValue = value[0];
    } else {
      inputValue = `${numSelectedTags} tags`;
    }
  }

  const input = (
    <StyledInput
      value={inputValue}
      placeholder={getPlaceholder()}
      allowClear={!isBottomNavigator}
      readOnly={true}
      $showClear={!!inputValue}
      $isSingleRowView={isSingleRowView}
      onChange={unselectAll}
      style={{ minWidth: 110, maxWidth: isSingleRowView ? 120 : "100%" }}
      $hasValue={!!inputValue}
    />
  );

  if (isChipsView) {
    return isBottomNavigator ? (
      <BottomDrawer title={title} triggerElement={input} content={content} />
    ) : (
      <Popover content={content} trigger={["click"]} placement="bottom">
        {input}
      </Popover>
    );
  }

  return content;
};

export default TagsFiltersSection;

interface TagButtonProps {
  name: TagName;
  isSelected: boolean;
  onClick: () => void;
}

const TagButton = ({ name, isSelected, onClick }: TagButtonProps) => {
  const theme = useTheme();
  const Icon = isSelected ? CheckOutlined : PlusOutlined;

  let tagStyle: CSSProperties = {
    display: "flex",
    alignItems: "center",
    width: "fit-content",
    fontSize: theme.typography.fontSizes.S,
    userSelect: "none",
  };
  if (!isSelected)
    tagStyle = {
      ...tagStyle,
      color: theme.colors.secondaryGrey,
      border: theme.borders.medium,
    };

  return (
    <Tag
      onClick={onClick}
      color={
        isSelected
          ? (theme.colors[TagNameToColor[name]] as string)
          : "transparent"
      }
      style={tagStyle}
    >
      <Name>{name.toUpperCase()}</Name>
      <Icon style={{ marginLeft: 6 }} />
    </Tag>
  );
};

const Name = styled.div`
  position: relative;
  top: 1px;
`;
